import React from 'react';
import { cn } from '../../lib/utils';

/**
 * SectionCard — unified panel surface.
 *
 * Props:
 *   title       string/ReactNode — rendered in the card header
 *   subtitle    string — smaller secondary line
 *   icon        Lucide icon for the header
 *   actions     ReactNode — right side of header (e.g. filter buttons)
 *   padding     'none' | 'sm' | 'md' (default 'md')
 *   hoverLift   boolean — enables translateY(-1px) on hover (default false)
 *   className   extra classes on the wrapper
 *   bodyClassName extra classes on the body
 */
const PADDING = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

const SectionCard = ({
  title = null,
  subtitle = '',
  icon: Icon = null,
  actions = null,
  padding = 'md',
  hoverLift = false,
  className = '',
  bodyClassName = '',
  children,
  ...rest
}) => {
  const hasHeader = title || actions;

  return (
    <section
      className={cn(
        'rounded-lg bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-800',
        'shadow-sm transition-[box-shadow,transform] duration-200 ease-out',
        hoverLift && 'hover:shadow-md hover:-translate-y-0.5 motion-reduce:transform-none',
        className,
      )}
      {...rest}
    >
      {hasHeader && (
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && (
              <span
                className="flex h-8 w-8 items-center justify-center rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 flex-shrink-0"
                aria-hidden="true"
              >
                <Icon size={16} />
              </span>
            )}
            <div className="min-w-0">
              {title && (
                <h2 className="text-base font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </header>
      )}

      <div className={cn(PADDING[padding] || PADDING.md, bodyClassName)}>{children}</div>
    </section>
  );
};

export default SectionCard;
